import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { cx } from '@/lib/clases'
import { formatearMonto, parsearMonto } from '@/lib/monto'
import { Boton } from '@/ui/Boton'
import { CampoTexto, CampoFormulario, Selector } from '@/ui/CampoFormulario'
import { CampoMonetario } from '@/ui/CampoMonetario'
import { InsigniaDiscreta, InsigniaEstado } from '@/ui/InsigniaEstado'
import { PanelLateral } from '@/ui/PanelLateral'
import { TablaDatos } from '@/ui/tabla/TablaDatos'
import { useFiltrosEnUrl } from '@/ui/tabla/filtrosEnUrl'
import { traducirErrorApi } from '@/dominio/documentos/errores'
import { TASAS_ITBIS, UNIDADES, etiquetaDeTasa, type Producto } from '@/dominio/productos/tipos'
import { cambioDesde, useCrearProducto, useEditarProducto, useProductos } from '@/dominio/productos/useProductos'
import { EncabezadoPantalla } from './-pantalla'

/**
 * Productos (E10).
 *
 * **El detalle que importa: la edición en línea del precio.** Doble clic en la
 * celda, Enter para guardar. "Un ferretero con 400 artículos no va a abrir 400
 * formularios para subir precios."
 *
 * Esta pantalla **reventaba con el primer producto del catálogo**: la columna de
 * ITBIS hacía `INDICADORES[producto.indicador].etiqueta` sobre un `indicador`
 * que el servidor no manda —el catálogo guarda la **tasa**—, y eso es un
 * `TypeError` en el render, no un guion. Con el catálogo vacío no se veía; con
 * un solo artículo, pantalla en blanco.
 */
export interface BusquedaProductos {
  codigo?: string
  descripcion?: string
  incluirAnulados?: string
}

export const Route = createFileRoute('/productos')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaProductos => ({
    codigo: typeof busqueda.codigo === 'string' ? busqueda.codigo : undefined,
    descripcion: typeof busqueda.descripcion === 'string' ? busqueda.descripcion : undefined,
    incluirAnulados: typeof busqueda.incluirAnulados === 'string' ? busqueda.incluirAnulados : undefined,
  }),
  component: PantallaProductos,
})

function PantallaProductos() {
  const { filtros, aplicar, limpiar, hayFiltrosActivos } = useFiltrosEnUrl({
    codigo: '',
    descripcion: '',
    incluirAnulados: '',
  })

  const [pagina, setPagina] = useState(1)
  const [tamano, setTamano] = useState(50)
  const [orden, setOrden] = useState<SortingState>([{ id: 'codigo', desc: false }])
  const [enEdicion, setEnEdicion] = useState<Producto | 'nuevo' | null>(null)
  const [editandoPrecio, setEditandoPrecio] = useState<string | null>(null)

  const consulta = useProductos(
    {
      codigo: filtros.codigo === '' ? undefined : filtros.codigo,
      descripcion: filtros.descripcion === '' ? undefined : filtros.descripcion,
      // Los tres filtros del extremo. Antes se mandaban `texto` y `estado`, que
      // el servidor ignora: el buscador devolvía siempre el catálogo entero.
      incluirAnulados: filtros.incluirAnulados === '' ? undefined : filtros.incluirAnulados === 'si',
    },
    pagina,
    tamano,
  )
  const editar = useEditarProducto()

  const columnas = useMemo<ColumnDef<Producto, unknown>[]>(
    () => [
      {
        id: 'codigo',
        accessorFn: (fila) => fila.codigo,
        header: 'Código',
        meta: { etiqueta: 'Código', numerico: true, ancho: '120px', exportar: (f) => f.codigo },
        cell: ({ row }) => <span className="mono text-gris-700">{row.original.codigo}</span>,
      },
      {
        id: 'descripcion',
        accessorFn: (fila) => fila.descripcion,
        header: 'Descripción',
        meta: { etiqueta: 'Descripción', exportar: (f) => f.descripcion },
        cell: ({ row }) => (
          <span className="block truncate text-gris-900" title={row.original.descripcion}>
            {row.original.descripcion}
          </span>
        ),
      },
      {
        id: 'unidad',
        accessorFn: (fila) => fila.unidadMedida ?? '',
        header: 'Unidad',
        meta: { etiqueta: 'Unidad', ancho: '130px', exportar: (f) => f.unidadMedida },
        cell: ({ row }) =>
          row.original.unidadMedida === null ? (
            // La representación impresa la exige en palabras. Sin ella el PDF
            // sale incompleto, así que se dice en vez de dejar la celda vacía.
            <span className="text-etiqueta text-ambar" title="La representación impresa la exige.">
              sin unidad
            </span>
          ) : (
            <span className="text-gris-700">{row.original.unidadMedida}</span>
          ),
      },
      {
        id: 'precio',
        accessorFn: (fila) => fila.precio,
        header: 'Precio',
        meta: { etiqueta: 'Precio', numerico: true, ancho: '150px', exportar: (f) => f.precio },
        cell: ({ row }) => (
          <CeldaDePrecio
            producto={row.original}
            editando={editandoPrecio === row.original.id}
            onAbrir={() => setEditandoPrecio(row.original.id)}
            onCerrar={() => setEditandoPrecio(null)}
            onGuardar={(precio) => {
              // El PUT reemplaza: va la ficha entera. Mandar solo el precio
              // borraba la unidad de medida y fallaba por falta de descripción.
              editar.mutate({ id: row.original.id, ...cambioDesde(row.original, { precio }) })
              setEditandoPrecio(null)
            }}
          />
        ),
      },
      {
        id: 'itbis',
        accessorFn: (fila) => fila.tasaItbis,
        header: 'ITBIS',
        enableSorting: false,
        meta: {
          etiqueta: 'ITBIS',
          ancho: '150px',
          exportar: (f) => etiquetaDeTasa(f.tasaItbis),
        },
        cell: ({ row }) => <InsigniaDiscreta>{etiquetaDeTasa(row.original.tasaItbis)}</InsigniaDiscreta>,
      },
      {
        id: 'estado',
        accessorFn: (fila) => (fila.anulado ? 'anulado' : 'activo'),
        header: 'Estado',
        enableSorting: false,
        meta: { etiqueta: 'Estado', ancho: '120px', exportar: (f) => (f.anulado ? 'anulado' : 'activo') },
        cell: ({ row }) =>
          row.original.anulado ? (
            <InsigniaEstado tono="neutral">Anulado</InsigniaEstado>
          ) : (
            <InsigniaEstado tono="verde">Activo</InsigniaEstado>
          ),
      },
    ],
    [editandoPrecio, editar],
  )

  const errorTraducido = consulta.isError ? traducirErrorApi(consulta.error) : null

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <EncabezadoPantalla
        titulo="Productos"
        descripcion="Doble clic en el precio para cambiarlo sin abrir el formulario."
        acciones={
          <Boton variante="primario" icono={<Plus />} onClick={() => setEnEdicion('nuevo')}>
            Nuevo
          </Boton>
        }
      />

      <TablaDatos<Producto>
        idTabla="productos"
        columnas={columnas}
        datos={consulta.data?.filas ?? []}
        obtenerId={(fila) => fila.id}
        cargando={consulta.isLoading}
        error={
          errorTraducido === null
            ? null
            : {
                titulo: errorTraducido.titulo,
                contexto: errorTraducido.contexto,
                detalleTecnico: errorTraducido.detalleTecnico,
              }
        }
        onReintentar={() => void consulta.refetch()}
        vacio={{
          variante: 'primera-vez',
          mensaje: 'Aún no tienes productos en el catálogo.',
          accion: (
            <Boton variante="primario" icono={<Plus />} onClick={() => setEnEdicion('nuevo')}>
              Nuevo
            </Boton>
          ),
        }}
        vacioFiltrado={{
          variante: 'sin-resultados',
          mensaje: 'Ningún producto con los filtros aplicados.',
          accion: (
            <Boton variante="secundario" tamano="menor" onClick={limpiar}>
              Limpiar filtros
            </Boton>
          ),
        }}
        orden={orden}
        onOrdenChange={setOrden}
        paginacion={{ pagina, tamano, total: consulta.data?.total ?? 0 }}
        onPaginacionChange={(siguiente) => {
          setPagina(siguiente.pagina)
          setTamano(siguiente.tamano)
        }}
        nombreExportacion="productos"
        hayFiltrosActivos={hayFiltrosActivos}
        onLimpiarFiltros={limpiar}
        filtros={
          <>
            {/* Dos campos, como en el extremo: código y descripción se buscan
                por separado y juntarlos obligaría a adivinar cuál quiso el
                usuario. */}
            <input
              type="search"
              value={filtros.codigo}
              onChange={(evento) => aplicar({ codigo: evento.target.value })}
              placeholder="Código"
              aria-label="Buscar productos por código"
              className="mono h-[var(--alto-control-menor)] w-[140px] rounded-md border border-gris-500 bg-white px-2 text-cuerpo text-gris-900"
            />
            <input
              type="search"
              value={filtros.descripcion}
              onChange={(evento) => aplicar({ descripcion: evento.target.value })}
              placeholder="Descripción"
              aria-label="Buscar productos por descripción"
              className="h-[var(--alto-control-menor)] w-[240px] rounded-md border border-gris-500 bg-white px-2 text-cuerpo text-gris-900"
            />
            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              <input
                type="checkbox"
                checked={filtros.incluirAnulados === 'si'}
                onChange={(evento) => aplicar({ incluirAnulados: evento.target.checked ? 'si' : '' })}
                className="size-4 accent-[var(--color-marca)]"
              />
              Incluir dados de baja
            </label>
          </>
        }
        onFilaClick={(fila) => {
          // Un clic en la fila mientras se edita el precio cerraría el campo.
          if (editandoPrecio === null) setEnEdicion(fila)
        }}
      />

      <PanelDeProducto producto={enEdicion} onCerrar={() => setEnEdicion(null)} />
    </div>
  )
}

/* ---------------------------------------------------------------------- */

/** Edición en línea: doble clic abre, Enter guarda, Esc cancela. */
function CeldaDePrecio({
  producto,
  editando,
  onAbrir,
  onCerrar,
  onGuardar,
}: {
  producto: Producto
  editando: boolean
  onAbrir: () => void
  onCerrar: () => void
  onGuardar: (precio: number) => void
}) {
  const [texto, setTexto] = useState(String(producto.precio))
  const campo = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!editando) return
    setTexto(String(producto.precio))
    campo.current?.focus()
    campo.current?.select()
  }, [editando, producto.precio])

  if (!editando) {
    return (
      <button
        type="button"
        onDoubleClick={(evento) => {
          evento.stopPropagation()
          onAbrir()
        }}
        title="Doble clic para cambiar el precio"
        className="numero w-full rounded-md px-1 text-right text-gris-900 hover:bg-gris-100"
      >
        {formatearMonto(producto.precio, { decimales: 2 })}
      </button>
    )
  }

  return (
    <input
      ref={campo}
      type="text"
      inputMode="decimal"
      aria-label={`Precio de ${producto.descripcion}`}
      value={texto}
      onClick={(evento) => evento.stopPropagation()}
      onChange={(evento) => setTexto(evento.target.value)}
      onBlur={onCerrar}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter') {
          evento.preventDefault()
          const numero = parsearMonto(texto)
          if (numero !== null) onGuardar(numero)
          else onCerrar()
        }
        if (evento.key === 'Escape') {
          evento.preventDefault()
          evento.stopPropagation()
          onCerrar()
        }
      }}
      className={cx('numero h-[26px] w-full rounded-md border border-marca bg-white px-1 text-gris-900')}
    />
  )
}

function PanelDeProducto({
  producto,
  onCerrar,
}: {
  producto: Producto | 'nuevo' | null
  onCerrar: () => void
}) {
  const esNuevo = producto === 'nuevo'
  const existente = producto === 'nuevo' || producto === null ? null : producto

  const [codigo, setCodigo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [unidad, setUnidad] = useState<string>(UNIDADES[0] ?? 'Unidad')
  const [precio, setPrecio] = useState<number | null>(null)
  const [tasaItbis, setTasaItbis] = useState(0.18)

  const crear = useCrearProducto()
  const editar = useEditarProducto()
  const enCurso = crear.isPending || editar.isPending

  useEffect(() => {
    if (producto === null) return
    setCodigo(existente?.codigo ?? '')
    setDescripcion(existente?.descripcion ?? '')
    setUnidad(existente?.unidadMedida ?? UNIDADES[0] ?? 'Unidad')
    setPrecio(existente?.precio ?? null)
    setTasaItbis(existente?.tasaItbis ?? 0.18)
    crear.reset()
    editar.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producto])

  const error = crear.isError
    ? traducirErrorApi(crear.error)
    : editar.isError
      ? traducirErrorApi(editar.error)
      : null

  const guardar = (): void => {
    const comun = { descripcion: descripcion.trim(), precio: precio ?? 0, tasaItbis, unidadMedida: unidad }
    if (existente !== null) {
      editar.mutate({ id: existente.id, ...comun }, { onSuccess: onCerrar })
      return
    }
    crear.mutate({ codigo: codigo.trim(), ...comun }, { onSuccess: onCerrar })
  }

  return (
    <PanelLateral
      abierto={producto !== null}
      onCerrar={onCerrar}
      titulo={esNuevo ? 'Nuevo producto' : (existente?.descripcion ?? 'Producto')}
      subtitulo={existente !== null ? existente.codigo : undefined}
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            disabled={codigo.trim() === '' || descripcion.trim() === '' || precio === null || enCurso}
            onClick={guardar}
          >
            {enCurso ? 'Guardando…' : 'Guardar'}
          </Boton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error !== null ? <p className="text-cuerpo text-rojo">{error.titulo}</p> : null}

        <CampoTexto
          etiqueta="Código"
          obligatorio
          value={codigo}
          disabled={existente !== null}
          onChange={(evento) => setCodigo(evento.target.value)}
          ayuda={existente !== null ? 'El código identifica la ficha y no se cambia.' : undefined}
        />
        <CampoTexto
          etiqueta="Descripción"
          obligatorio
          value={descripcion}
          onChange={(evento) => setDescripcion(evento.target.value)}
        />

        <CampoFormulario etiqueta="Unidad de medida" ayuda="En palabras: la impresa la exige así.">
          {(atributos) => (
            <Selector {...atributos} value={unidad} onChange={(evento) => setUnidad(evento.target.value)}>
              {UNIDADES.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </Selector>
          )}
        </CampoFormulario>

        <CampoMonetario
          etiqueta="Precio unitario"
          obligatorio
          valor={precio}
          onValorChange={setPrecio}
          decimales={4}
          ayuda="Cuatro decimales, como exige el formato de DGII."
        />

        <CampoFormulario
          etiqueta="ITBIS"
          // El catálogo guarda la tasa, no el indicador: una tasa de cero puede
          // ser tasa cero o exento, y esa distinción la decide la línea de la
          // factura, no la ficha del artículo.
          ayuda="El catálogo guarda la tasa. Si es cero, el indicador de la línea (tasa cero o exento) se elige al facturar."
        >
          {(atributos) => (
            <Selector
              {...atributos}
              value={String(tasaItbis)}
              onChange={(evento) => setTasaItbis(Number(evento.target.value))}
            >
              {TASAS_ITBIS.map((tasa) => (
                <option key={tasa.valor} value={String(tasa.valor)}>
                  {tasa.etiqueta}
                </option>
              ))}
            </Selector>
          )}
        </CampoFormulario>
      </div>
    </PanelLateral>
  )
}
